export interface RouterOptions {
    /**
     * "history" (default): path/params/hash live in the real URL via the
     * History API. "embedded": the app is mounted inside a partner host page
     * (PTC-3919) — the router never touches window.history/location; the
     * current route is fed in by the integration wrapper via setRoute() and
     * app-initiated navigation is reported outward via the "embedded-navigate"
     * event.
     */
    mode?: "history" | "embedded";
}

interface EmbeddedRoute {
    path: string;
    params: Record<string, string>;
    hash: string;
}

export class Router {
    history: string[] = [];

    readonly mode: "history" | "embedded";

    private _eventDispatcher = document.createElement("div");

    private _forceNextUpdate = false;

    private _embedded: EmbeddedRoute = { path: "", params: {}, hash: "" };

    private _popstateHandler = () => {
        this._pathChanged();
    };

    addEventListener(name: string, handler: (e: Event) => void) {
        this._eventDispatcher.addEventListener(name, handler);
    }

    removeEventListener(name: string, handler: (e: Event) => void) {
        this._eventDispatcher.removeEventListener(name, handler);
    }

    /**
     * Detaches the router from the window. Without this every embedded
     * dispose/re-init cycle (PTC-3919) would stack another permanent popstate
     * listener on the host page.
     */
    destroy() {
        window.removeEventListener("popstate", this._popstateHandler);
    }

    constructor(
        public basePath = "/",
        { mode = "history" }: RouterOptions = {}
    ) {
        this.mode = mode;
        if (this.mode === "embedded") {
            // The initial route arrives via the wrapper's first setRoute().
            return;
        }
        window.addEventListener("popstate", this._popstateHandler);
        this._pathChanged();
    }

    /**
     * Embedded-mode input channel for the integration wrapper: feeds the route
     * chosen by the host ("path?query#hash"). No-ops when the parsed route
     * equals the current one — the value backstop against host↔app echo loops;
     * the structural protection is that host-fed changes never fire
     * "embedded-navigate".
     */
    setRoute(route: string) {
        if (this.mode !== "embedded") {
            throw new Error("setRoute() is only available in embedded router mode");
        }
        const next = parseRoute(route);
        if (routesEqual(next, this._embedded)) {
            return;
        }
        this._applyEmbedded(next, { replace: false, external: true });
    }

    private _pathChanged() {
        const index = (history.state && history.state.historyIndex) || 0;
        const path = this.path;
        const direction = this.history.length - 1 < index ? "forward" : "backward";

        if (this.history.length === index) {
            this.history.push(path);
        } else
            while (this.history.length - 1 > index) {
                this.history.pop();
            }

        const canceled =
            !this._forceNextUpdate &&
            !this._eventDispatcher.dispatchEvent(
                new CustomEvent("before-route-changed", { detail: { path, direction }, cancelable: true })
            );

        if (canceled) {
            this._forceNextUpdate = true;
            direction === "forward" ? this.back() : this.forward();
            return;
        } else {
            this._forceNextUpdate = false;
        }

        this._eventDispatcher.dispatchEvent(new CustomEvent("route-changed", { detail: { path, direction } }));

        // Scroll to hash if present
        if (this.hash) {
            this._scrollToHash(this.hash, 0);
        }
    }

    /**
     * Single commit point for embedded-mode route changes. `external` marks
     * host-fed changes (setRoute) — only internal changes (go, params setter,
     * back) notify the wrapper via "embedded-navigate".
     */
    private _applyEmbedded(
        next: EmbeddedRoute,
        { replace, external, direction = "forward" }: { replace: boolean; external: boolean; direction?: string }
    ): boolean {
        const canceled = !this._eventDispatcher.dispatchEvent(
            new CustomEvent("before-route-changed", { detail: { path: next.path, direction }, cancelable: true })
        );

        if (canceled) {
            // Nothing was committed. For a host-driven change, report the
            // still-current route so the host bounces its URL back (the
            // embedded equivalent of the standalone history.back() bounce);
            // the host's re-set of the attribute then hits setRoute()'s
            // equality no-op.
            if (external) {
                this._dispatchEmbeddedNavigate(this._embedded, true);
            }
            return false;
        }

        this._embedded = next;
        if (replace && this.history.length) {
            this.history[this.history.length - 1] = next.path;
        } else {
            this.history.push(next.path);
        }

        // Notify the wrapper before route-changed: a route-changed listener may
        // apply route defaults via the params setter (setDefaults in the
        // Routing mixin), which fires its own embedded-navigate — that one has
        // to land last so the host ends up with the defaults, mirroring the
        // standalone pushState-then-replaceState order.
        if (!external) {
            this._dispatchEmbeddedNavigate(next, replace);
        }

        this._eventDispatcher.dispatchEvent(
            new CustomEvent("route-changed", { detail: { path: next.path, direction } })
        );

        if (next.hash) {
            this._scrollToHash(next.hash, 0);
        }

        return true;
    }

    /** Tells the integration wrapper that the app refused `path` and redirected elsewhere. */
    reportDeniedRoute(path: string) {
        this._eventDispatcher.dispatchEvent(new CustomEvent("embedded-route-denied", { detail: { path } }));
    }

    private _dispatchEmbeddedNavigate({ path, params, hash }: EmbeddedRoute, replace: boolean) {
        this._eventDispatcher.dispatchEvent(
            new CustomEvent("embedded-navigate", { detail: { path, params: { ...params }, hash, replace } })
        );
    }

    private _scrollToHash(hash: string, delay: number) {
        // Delay to ensure DOM is updated
        setTimeout(() => {
            const elementId = hash.substring(1); // Remove the # character
            const element = this._findElementById(elementId);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, delay);
    }

    /**
     * Find an element by ID, searching through shadow DOM boundaries
     */
    private _findElementById(id: string): Element | null {
        // First try regular DOM
        const element = document.getElementById(id);
        if (element) {
            return element;
        }

        // If not found, search through all shadow roots
        return this._searchShadowRoots(document.body, id);
    }

    /**
     * Recursively search through shadow roots for an element with the given ID
     */
    private _searchShadowRoots(root: Element, id: string): Element | null {
        // Check if this element has a shadow root
        if (root.shadowRoot) {
            // Try to find the element in this shadow root
            const element = root.shadowRoot.getElementById(id);
            if (element) {
                return element;
            }

            // Recursively search child elements in the shadow root
            const shadowChildren = root.shadowRoot.querySelectorAll("*");
            for (const child of shadowChildren) {
                const found = this._searchShadowRoots(child, id);
                if (found) {
                    return found;
                }
            }
        }

        // Recursively search regular DOM children
        const children = root.querySelectorAll("*");
        for (const child of children) {
            const found = this._searchShadowRoots(child, id);
            if (found) {
                return found;
            }
        }

        return null;
    }

    /**
     * Manually scroll to hash
     * Useful when content is loaded asynchronously.
     */
    scrollToHash(hash: string) {
        this._scrollToHash(hash, 0);
    }

    get path() {
        if (this.mode === "embedded") {
            return this._embedded.path;
        }
        return window.location.pathname.replace(new RegExp("^" + this.basePath), "");
    }

    get hash() {
        if (this.mode === "embedded") {
            return this._embedded.hash;
        }
        return window.location.hash;
    }

    get params() {
        if (this.mode === "embedded") {
            return { ...this._embedded.params };
        }
        const params: Record<string, string> = {};
        for (const [key, value] of new URLSearchParams(window.location.search)) {
            params[key] = value;
        }
        return params;
    }

    set params(params: { [prop: string]: string }) {
        if (this.mode === "embedded") {
            // Like the standalone replaceState below, this is deliberately
            // silent (no route-changed) — setDefaults() in the Routing mixin
            // relies on that; the wrapper still learns about it.
            this._embedded = { ...this._embedded, params: { ...params } };
            if (this.history.length === 0) {
                this.history.push(this._embedded.path);
            }
            this._dispatchEmbeddedNavigate(this._embedded, true);
            return;
        }
        history.replaceState(
            { historyIndex: this.history.length - 1 },
            "",
            this.basePath + this.path + "?" + new URLSearchParams(params).toString() + this.hash
        );
    }

    get canGoBack() {
        return this.history.length > 1;
    }

    go(path: string = this.path, params?: { [prop: string]: string }, replace = false) {
        // Extract hash from path if present
        let hash = "";
        let cleanPath = path;
        const hashIndex = path.indexOf("#");
        if (hashIndex !== -1) {
            hash = path.substring(hashIndex);
            cleanPath = path.substring(0, hashIndex);
        }

        if (this.mode === "embedded") {
            const next: EmbeddedRoute = { path: cleanPath, params: { ...(params || {}) }, hash };
            if (routesEqual(next, this._embedded)) {
                return;
            }
            this._applyEmbedded(next, { replace, external: false });
            return;
        }

        const queryString = new URLSearchParams(params || {}).toString();
        const currentHash = window.location.hash;

        if (cleanPath !== this.path || queryString !== window.location.search.substring(1) || hash !== currentHash) {
            let url = this.basePath + cleanPath;
            if (queryString) {
                url += "?" + queryString;
            }
            if (hash) {
                url += hash;
            }
            if (replace) {
                history.replaceState({ historyIndex: this.history.length - 1 }, "", url);
            } else {
                history.pushState({ historyIndex: this.history.length }, "", url);
            }
            this._pathChanged();
        }
    }

    redirect(path: string) {
        return this.go(path, undefined, true);
    }

    forward() {
        if (this.mode === "embedded") {
            // Only used by the standalone cancel-and-bounce mechanism, which
            // embedded mode replaces with a report-old-route bounce.
            return;
        }
        history.forward();
    }

    back() {
        if (this.mode === "embedded") {
            if (!this.canGoBack) {
                return false;
            }
            const previous = this.history[this.history.length - 2];
            // Params are not restored — matches standalone, whose history
            // array also only stores path strings.
            const committed = this._applyEmbedded(
                { path: previous, params: {}, hash: "" },
                { replace: true, external: false, direction: "backward" }
            );
            if (committed) {
                this.history.pop();
            }
            return committed;
        }
        if (this.canGoBack) {
            history.back();
            return true;
        }
        return false;
    }
}

function parseRoute(route: string): EmbeddedRoute {
    let rest = route;
    let hash = "";
    const hashIndex = rest.indexOf("#");
    if (hashIndex !== -1) {
        hash = rest.substring(hashIndex);
        rest = rest.substring(0, hashIndex);
    }
    let path = rest;
    const params: Record<string, string> = {};
    const queryIndex = rest.indexOf("?");
    if (queryIndex !== -1) {
        path = rest.substring(0, queryIndex);
        for (const [key, value] of new URLSearchParams(rest.substring(queryIndex + 1))) {
            params[key] = value;
        }
    }
    return { path, params, hash };
}

function routesEqual(a: EmbeddedRoute, b: EmbeddedRoute) {
    if (a.path !== b.path || a.hash !== b.hash) {
        return false;
    }
    const aKeys = Object.keys(a.params);
    const bKeys = Object.keys(b.params);
    return aKeys.length === bKeys.length && aKeys.every((key) => a.params[key] === b.params[key]);
}
